import { ApplicationConfig, APP_INITIALIZER } from '@angular/core';
import { NormsCacheService } from './services/norms-cache.service';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { appInsightsInterceptor } from './interceptors/app-insights.interceptor';
import { provideAnimations } from '@angular/platform-browser/animations';
import { 
  MSAL_INSTANCE, 
  MsalService,
  MsalBroadcastService
} from '@azure/msal-angular';
import { 
  PublicClientApplication, 
  BrowserCacheLocation,
  IPublicClientApplication,
  LogLevel
} from '@azure/msal-browser';
import { routes } from './app.routes';
import { AAD_TENANT_ID as TENANT_ID, AAD_CLIENT_ID as CLIENT_ID } from './config/auth-config';

// TENANT_ID / CLIENT_ID are the single source of truth in ./config/auth-config.ts
// (shared with the auth service so the exposed API scope stays consistent).
// TENANT_ID must match the tenant in staticwebapp.config.json (openIdIssuer).

/**
 * MSAL Instance Factory
 * Creates and configures the PublicClientApplication
 */
export function msalInstanceFactory(): IPublicClientApplication {
  if (!CLIENT_ID) {
    console.warn('⚠️  CLIENT_ID not configured. Auth will not work.\n\nTo fix:\n1. Go to Azure Portal > App registrations > nest-ui-swa-non-prod\n2. Copy the "Application (client) ID"\n3. Replace the CLIENT_ID value in src/app/app.config.ts (line ~20)');
  }

  return new PublicClientApplication({
    auth: {
      clientId: CLIENT_ID || 'placeholder',
      authority: `https://login.microsoftonline.com/${TENANT_ID}`,
      redirectUri: window.location.origin,
      // Silent token refresh uses a dedicated blank page (auth-redirect.html),
      // passed explicitly as redirectUri on acquireTokenSilent/ssoSilent, so
      // X-Frame-Options: DENY on the main app URL does not block the silent iframe.
      // Both origin and auth-redirect.html must be registered as SPA redirect URIs
      // in the Entra app registration.
      postLogoutRedirectUri: window.location.origin
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage
    },
    system: {
      // MSAL v5 silent iframe: the redirect page (auth-redirect.html) is a blank
      // page that does NOT run MSAL's redirect_bridge, so the internal
      // BroadcastChannel handshake never completes and the iframe always times
      // out. That is expected — AuthService catches the timeout and falls back to
      // a full-page loginRedirect (which establishes a refresh token so later
      // silent calls use the RT network path, not the iframe). Keep this timeout
      // short so that first-load fallback is fast instead of a 10s stall.
      iframeBridgeTimeout: 3000,
      loggerOptions: {
        logLevel: LogLevel.Warning,
        piiLoggingEnabled: false,
        loggerCallback: (level, message) => {
          if (level <= LogLevel.Warning) console.warn('[MSAL]', message);
        }
      }
    }
  });
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideHttpClient(withInterceptors([appInsightsInterceptor])),
    provideAnimations(),
    
    // MSAL Configuration
    {
      provide: MSAL_INSTANCE,
      useFactory: msalInstanceFactory
    },
    MsalService,
    MsalBroadcastService,

    // Pre-flight: validate IndexedDB cache against server version
    {
      provide: APP_INITIALIZER,
      useFactory: (cacheService: NormsCacheService) => () => cacheService.initCache(),
      deps: [NormsCacheService],
      multi: true
    }
  ]
};
